// The guide on /docs: what Brainyard Share is, how to install and use it.
// Content as data, src/locales/<language>/docs.json — its sections under a
// few groups, each section paragraphs, sub-headings, lists, tables, commands
// and notes, with inline `code` and [links](url) only — built as React
// elements by pages/Docs.tsx, never parsed as HTML, and never read through
// i18next, so `{{app-name}}` is shown as written. Every language has the same
// groups, sections and blocks (i18n.test.ts). RFC-043 and RFC-044 are what
// this describes.
import { language } from "./i18n";
import { bundles } from "./i18n/resources";

export type DocBlock =
  | { kind: "p"; text: string }
  | { kind: "heading"; id: string; text: string }
  | { kind: "list"; items: string[] }
  | { kind: "code"; text: string }
  | { kind: "note"; text: string }
  | { kind: "table"; head: string[]; rows: string[][] };

export interface DocSection {
  id: string;
  title: string;
  blocks: DocBlock[];
}

/** Sections that belong together, by id, in the order they are read; every section is in one. */
export interface DocGroup {
  id: string;
  title: string;
  sections: string[];
}

interface Guide {
  groups: DocGroup[];
  sections: DocSection[];
}

const guide = () => (bundles[language()]?.docs ?? bundles.en!.docs) as Guide;

/** The guide's sections, in the language shown. */
export function docs(): DocSection[] {
  return guide().sections;
}

/** The guide's groups, each with its sections, in the language shown. */
export function docGroups(): (Omit<DocGroup, "sections"> & { sections: DocSection[] })[] {
  const { groups, sections } = guide();
  const byId = new Map(sections.map((s) => [s.id, s]));
  return groups.map((g) => ({ ...g, sections: g.sections.flatMap((id) => byId.get(id) ?? []) }));
}

/**
 * A code block's lines as commands to copy: each line one command, what
 * follows two spaces and a `#` its note, shown beside it and never copied.
 * A code block is commands and nothing else — no output, no line continued
 * with `\`, no line that is only a comment, no note after one space — which
 * docs.test.tsx holds every language's guide to.
 */
export function commandLines(text: string): { command: string; note?: string }[] {
  return text
    .split("\n")
    .filter((l) => l.trim() !== "")
    .map((l) => {
      const m = /^(.*?\S)\s{2,}#\s*(.*)$/.exec(l);
      return m ? { command: m[1]!, note: m[2]! } : { command: l.trimEnd() };
    });
}

/** Inline text: `code`, **bold** and [label](url) runs, the rest plain. */
export type Inline = { kind: "text" | "code" | "bold"; text: string } | { kind: "link"; text: string; href: string };

export function inline(s: string): Inline[] {
  const out: Inline[] = [];
  const re = /`([^`]+)`|\*\*([^*]+)\*\*|\[([^\]]+)\]\(([^)\s]+)\)/g;
  let last = 0;
  for (let m = re.exec(s); m; m = re.exec(s)) {
    if (m.index > last) out.push({ kind: "text", text: s.slice(last, m.index) });
    if (m[1] !== undefined) out.push({ kind: "code", text: m[1] });
    else if (m[2] !== undefined) out.push({ kind: "bold", text: m[2] });
    else out.push({ kind: "link", text: m[3]!, href: m[4]! });
    last = re.lastIndex;
  }
  if (last < s.length) out.push({ kind: "text", text: s.slice(last) });
  return out;
}
